import {
  Box,
  Divider,
  Tab,
  Tabs,
  styled,
  Typography,
  Select,
  MenuItem,
  Button,
  SelectChangeEvent,
  TextField,
  InputAdornment,
} from '@mui/material';
import theme from '../../../theme';
import StoreDetails from './StoreDetail';
import GeneralSupport from '../../../pages/knowledgeBase/GeneralSupport';
import { useState } from 'react';
import { CloudArrowUp, Plus, MagnifyingGlass } from '@phosphor-icons/react';
import KnowledgeBaseFileUploadModal from '../../../components/modals/KnowledgeBaseFileUploadModal';
import QuestionnaireDrawer from '../../../components/GeneralSupportFlow/QuestionnaireDrawer';
import { useAlert } from '../../../hooks/useAlert';
import { IStoreKnowledgeRequest } from '../../../schemas/apis/interface';
import useCreateStoreKnowledge from '../../../hooks/useCreateStoreKB';
import { useQueryClient } from '@tanstack/react-query';
import RestaurantMenuConfig from '../../../pages/knowledgeBase/RestaurantMenuConfig';
import MenuConfigurationDrawer from '../MenuConfigurationDrawer';
import Reservation from '../../../pages/knowledgeBase/Reservation';
import HotelConfigurationDrawer from '../HotelConfigurationDrawer';
const StyledTabs = styled(Tabs)({
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  '& .MuiTab-root': {
    textTransform: 'none',
    fontWeight: 400,
    fontSize: '16px',
    color: 'text.secondary',
    '&.Mui-selected': {
      color: theme.palette.custom.textBlue,
      fontWeight: 500,
      backgroundColor: theme.palette.custom.sidebarActiveBg,
      borderRadius: '6px',
    },
  },
});

interface TabPanelProps {
  children: React.ReactNode;
  value: number;
  index: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => {
  return value === index ? <>{children}</> : null;
};

interface StoreDetailsTabsProps {
  store: any;
  brandsList: any;
  selectedTab: number;
  onTabChange: (value: number) => void;
}

const StoreDetailsTabs = ({
  store,
  brandsList,
  selectedTab,
  onTabChange,
}: StoreDetailsTabsProps) => {
  const [selectedMethod, setSelectedMethod] = useState<string>('file-upload');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isQuestionnaireDrawerOpen, setIsQuestionnaireDrawerOpen] =
    useState(false);
  const [isMenuConfigDrawerOpen, setIsMenuConfigDrawerOpen] = useState(false);
  const [isHotelConfigDrawerOpen, setIsHotelConfigDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const { showAlert } = useAlert();
  const { createStoreKnowledge, isPending } = useCreateStoreKnowledge();
  const queryClient = useQueryClient();

  const tabItems = [
    {
      label: 'Details',
      component: <StoreDetails store={store} brandsList={brandsList} />,
    },
    {
      label: 'Menu',
      component: (
        <RestaurantMenuConfig
          store={store}
          fromStoreDetails={true}
          searchQuery={searchQuery}
        />
      ),
    },
    {
      label: 'Reservation',
      component: <Reservation store={store} fromStoreDetails={true} />,
    },
    {
      label: 'General FAQ',
      component: (
        <GeneralSupport selectedStore={store} selectedMethod={selectedMethod} />
      ),
    },
  ];

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    onTabChange(newValue);
  };

  const handleMethodChange = (event: SelectChangeEvent) => {
    setSelectedMethod(event.target.value);
  };

  const handleSaveQuestions = (questions: any[]) => {
    try {
      const formattedData: IStoreKnowledgeRequest = {
        stores: [
          {
            storeId: store?.id,
            storeAlias: store?.friendly_name,
            knowledgeBase: {
              method: 'quesandAnswer',
              qna: questions.map((q, index) => ({
                id: (store?.storeknowledgeBase?.qna?.length || 0) + index + 1,
                ques: q.ques || q.question,
                ans: q.ans || q.answer,
              })),
            },
          },
        ],
      };

      createStoreKnowledge(formattedData, {
        onSuccess: () => {
          setIsQuestionnaireDrawerOpen(false);
          showAlert('Questions added successfully', 'success');
          queryClient.invalidateQueries({ queryKey: ['stores'] });
        },
        onError: (error: any) => {
          showAlert(
            error.response?.data?.message || 'Error adding questions',
            'error'
          );
        },
      });
    } catch (error) {
      console.error('Error saving questionnaire:', error);
      showAlert('Error adding questions', 'error');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: '#FFF',
        borderRadius: '8px',
        border: '1px solid #DCE0E7',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          padding: '10px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <StyledTabs value={selectedTab} onChange={handleTabChange}>
          {tabItems.map((tab, i) => (
            <Tab key={i} label={tab.label} />
          ))}
        </StyledTabs>

        {selectedTab === 2 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="text"
              sx={{ textTransform: 'none', boxShadow: 'none' }}
              onClick={() => setIsHotelConfigDrawerOpen(true)}
            >
              Configure Reservation Details
            </Button>
          </Box>
        )}

        {selectedTab === 3 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
              }}
            >
              <Typography
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '12px',
                  fontWeight: 400,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                Knowledge Base Methods
              </Typography>
              <Select
                value={selectedMethod}
                onChange={handleMethodChange}
                sx={{
                  '& .MuiSelect-select': {
                    display: 'flex',
                    padding: '8px 12px',
                    alignItems: 'center',
                    borderRadius: '6px',
                    border: '1px solid #DCE0E7',
                    background: '#F8F9FC',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: 'normal',
                    letterSpacing: '-0.14px',
                    color: theme.palette.lightGray.text,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                  minWidth: '140px',
                }}
              >
                <MenuItem value="file-upload">Upload File</MenuItem>
                <MenuItem value="questionnaire">Questionnaire</MenuItem>
              </Select>
            </Box>

            {selectedMethod === 'file-upload' && (
              <Button
                variant="contained"
                startIcon={<CloudArrowUp size={20} />}
                onClick={() => setIsModalOpen(true)}
                sx={{ textTransform: 'none', boxShadow: 'none' }}
              >
                Upload File
              </Button>
            )}
            {selectedMethod === 'questionnaire' && (
              <Button
                variant="contained"
                startIcon={<Plus size={20} />}
                onClick={() => setIsQuestionnaireDrawerOpen(true)}
                sx={{ textTransform: 'none', boxShadow: 'none' }}
              >
                New Question
              </Button>
            )}
          </Box>
        )}
        {selectedTab === 1 &&
          (store.is_menu_configured ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                placeholder="Search menu items..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MagnifyingGlass size={20} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: '300px',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#F8F9FC',
                    borderRadius: '6px',
                    '& fieldset': {
                      borderColor: '#DCE0E7',
                    },
                    '&:hover fieldset': {
                      borderColor: '#DCE0E7',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
              <Button
                variant="text"
                onClick={() => setIsMenuConfigDrawerOpen(true)}
                sx={{ textTransform: 'none', boxShadow: 'none' }}
              >
                View Menu Config
              </Button>
            </Box>
          ) : (
            <Button
              variant="text"
              onClick={() => setIsMenuConfigDrawerOpen(true)}
              sx={{ textTransform: 'none', boxShadow: 'none' }}
            >
              Configure Menu
            </Button>
          ))}
      </Box>
      <Divider />
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        {tabItems.map((tab, index) => (
          <TabPanel key={index} value={selectedTab} index={index}>
            {tab.component}
          </TabPanel>
        ))}
      </Box>

      {/* Modals */}
      <KnowledgeBaseFileUploadModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedStore={store}
      />

      <QuestionnaireDrawer
        open={isQuestionnaireDrawerOpen}
        onClose={() => setIsQuestionnaireDrawerOpen(false)}
        selectedStore={{
          id: store?.id,
          friendly_name: store?.friendly_name,
          existingQuestionsCount: store?.storeknowledgeBase?.qna?.length || 0,
        }}
        onSave={handleSaveQuestions}
        isPending={isPending}
      />
      {/* <StoreMenu
        store={store}
        onAddStoreMenu={onAddStoreMenu}
        
      /> */}
      <MenuConfigurationDrawer
        store={store}
        open={isMenuConfigDrawerOpen}
        onClose={() => setIsMenuConfigDrawerOpen(false)}
      />
      <HotelConfigurationDrawer
        store={store}
        open={isHotelConfigDrawerOpen}
        onClose={() => setIsHotelConfigDrawerOpen(false)}
      />
    </Box>
  );
};

export default StoreDetailsTabs;
